import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/Layout";
import { initErrorReporter } from "@/lib/errorReporter";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CompanyDetailPage from "@/pages/CompanyDetailPage";
import LeadsPage from "@/pages/LeadsPage";
import TeamPage from "@/pages/TeamPage";
import TemplatesPage from "@/pages/TemplatesPage";
import WebsiteBuilderPage from "@/pages/WebsiteBuilderPage";
import SetupPasswordPage from "@/pages/SetupPasswordPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import SettingsPage from "@/pages/SettingsPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import LiveSitesPage from "@/pages/LiveSitesPage";
import { SipPhoneProvider } from "@/components/sales/SipPhoneProvider";
import ScrollToTop from "@/components/ScrollToTop";

// Public website pages
import HomePage from "@/pages/public/HomePage";
import PreisePage from "@/pages/public/PreisePage";
import PortfolioPage from "@/pages/public/PortfolioPage";
import PublicDemoPage from "@/pages/public/PublicDemoPage";
import LoesungenPage from "@/pages/public/LoesungenPage";
import UeberUnsPage from "@/pages/public/UeberUnsPage";
import KontaktPage from "@/pages/public/KontaktPage";
import KundenPortalPage from "@/pages/public/KundenPortalPage";
import ComparePage from "@/pages/public/ComparePage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-sm text-muted-foreground">Laden...</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public website routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/loesungen" element={<LoesungenPage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/leistungen" element={<PreisePage />} />
      <Route path="/preise" element={<Navigate to="/leistungen" replace />} />
      <Route path="/ueber-uns" element={<UeberUnsPage />} />
      <Route path="/kontakt" element={<KontaktPage />} />
      <Route path="/portal" element={<KundenPortalPage />} />

      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup/:token" element={<SetupPasswordPage />} />
      <Route path="/demo/:slug" element={<PublicDemoPage />} />
      <Route path="/compare/:token" element={<ComparePage />} />
      <Route path="/k/:token" element={<ComparePage />} />

      {/* Protected app routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/firmen" element={<ProtectedRoute><CompaniesPage /></ProtectedRoute>} />
      <Route path="/firmen/:id" element={<ProtectedRoute><CompanyDetailPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/termine" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/integrationen" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/einstellungen" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path="/builder/:id" element={<ProtectedRoute><WebsiteBuilderPage /></ProtectedRoute>} />
      <Route path="/live-sites" element={<ProtectedRoute><LiveSitesPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Background-Error-Reporter (window.error / unhandledrejection /
  // React-Hydration-Warnings) — postet an /api/_internal/client-errors.
  // Keine UI, läuft komplett im Hintergrund.
  useEffect(() => { initErrorReporter(); }, []);
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <SipPhoneProvider>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </SipPhoneProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
